import { fn } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { pluralize } from 'ember-inflector';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import UiPill from 'garaje/components/ui-pill';
import VisitorProfileHoverCard from 'garaje/components/visitors/profiles/hover-card';
import removedString from 'garaje/helpers/removed-string';
import type Invite from 'garaje/models/invite';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { shouldShowVisitorProfileHoverCard } from 'garaje/utils/visitor-profile-hover-card';
import { reads } from 'macro-decorators';

interface InvitesTableInviteListNameCellSignature {
  Args: {
    delay?: number;
    lineHeight?: number;
    lines?: number;
    model?: Invite;
    popperContainer?: string;
    route?: string;
    text?: string;
    tooltipClass?: string;
  };
  Blocks: {
    default: [data: string];
  };
  Element: HTMLTableCellElement;
}

export default class InvitesTableInviteListNameCell extends Component<InvitesTableInviteListNameCellSignature> {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked textWasTruncated: boolean = false;

  @reads('args.delay', 400) delay!: number;
  @reads('args.lineHeight', 1) lineHeight!: number;
  @reads('args.lines', 1) lines!: number;
  @reads('args.popperContainer', '.ui__page') popperContainer!: string;
  @reads('args.route', 'visitors.invites.show') route!: string;
  @reads('args.tooltipClass', 'text-sm font-bold break-word max-w-sm z-50') tooltipClass!: string;

  contentElementId = guidFor(this);

  get data(): string {
    const { text, model } = this.args;

    return text || model?.fullName || '';
  }

  get visitorProfile(): VisitorProfileModel | null {
    return this.args.model?.visitorProfile ?? null;
  }

  get showHoverCard(): boolean {
    return shouldShowVisitorProfileHoverCard({
      visitorProfile: this.visitorProfile,
      recordId: this.args.model?.id,
      featureFlags: this.featureFlags,
      abilities: this.abilities,
    });
  }

  get inviteId(): string {
    // Safe due to showHoverCard guard above; narrow to satisfy hover-card signature.
    return this.args.model?.id ?? '';
  }

  <template>
    {{#if (has-block)}}
      {{yield (removedString this.data)}}
    {{else}}
      <th ...attributes data-test-invites-list-name-cell>
        {{#if this.showHoverCard}}
          {{! showHoverCard is only true when visitorProfile is non-null }}
          <VisitorProfileHoverCard
            @visitorProfile={{this.visitorProfile}}
            @from="invites"
            @id={{this.inviteId}}
            @fallbackName={{removedString this.data}}
            @visitRoute={{this.route}}
            @popperContainer={{this.popperContainer}}
          >
            <LinkTo
              @route={{this.route}}
              @model={{@model.id}}
              class="flex items-start text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
              data-test-invite-link-to={{@model.id}}
            >
              <LineClamp
                @text={{removedString this.data}}
                @lines={{this.lines}}
                @lineHeight={{this.lineHeight}}
                aria-hidden="true"
                data-test-invites-list-name-cell-content
              />
              {{#if @model.hasAdditionalGuests}}
                <div aria-hidden="true">
                  <UiPill class="whitespace-nowrap ml-1 mt-[-0.5em]" @fill="grayDark" data-test-additional-guests>
                    +{{@model.additionalGuests}}
                    {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
                  </UiPill>
                </div>
              {{/if}}
              {{! No truncated-name EmberPopover here — the hover card shows the full name in its header,
                  and stacking two popovers on the same trigger causes flicker/overlap. The truncate
                  signal is unwired in this branch (no consumer), so @handleTruncate is omitted to
                  avoid orphaned writes to @tracked state. }}

              <div class="sr-only">
                {{! Full visitor name available to screen reader }}
                Visitor:
                <span data-test-invite-full-name>{{removedString this.data}}</span>
                {{#if @model.hasAdditionalGuests}}
                  and
                  {{@model.additionalGuests}}
                  {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
                {{/if}}.
              </div>
            </LinkTo>
          </VisitorProfileHoverCard>
        {{else}}
          <LinkTo
            @route={{this.route}}
            @model={{@model.id}}
            class="flex items-start text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
            data-test-invite-link-to={{@model.id}}
          >
            <LineClamp
              @text={{removedString this.data}}
              @lines={{this.lines}}
              @lineHeight={{this.lineHeight}}
              @handleTruncate={{fn (mut this.textWasTruncated)}}
              aria-hidden="true"
              data-test-invites-list-name-cell-content
            />
            {{#if @model.hasAdditionalGuests}}
              <div aria-hidden="true">
                <UiPill class="whitespace-nowrap ml-1 mt-[-0.5em]" @fill="grayDark" data-test-additional-guests>
                  +{{@model.additionalGuests}}
                  {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
                </UiPill>
              </div>
            {{/if}}
            {{#if this.textWasTruncated}}
              <EmberPopover
                @tooltipClass={{this.tooltipClass}}
                @popperContainer={{this.popperContainer}}
                @delay={{this.delay}}
                @text={{removedString this.data}}
                data-test-invites-list-name-cell-tooltip
              />
            {{/if}}

            <div class="sr-only">
              {{! Full visitor name available to screen reader }}
              Visitor:
              <span data-test-invite-full-name>{{removedString this.data}}</span>
              {{#if @model.hasAdditionalGuests}}
                and
                {{@model.additionalGuests}}
                {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
              {{/if}}.
            </div>
          </LinkTo>
        {{/if}}
      </th>
    {{/if}}
  </template>
}
